import React, { Fragment, useMemo, useRef, useCallback } from 'react';

import styled from 'styled-components';
import Slider from 'react-slick';
import ReactTooltip from 'react-tooltip';

import orderBy from 'lodash/orderBy';
import useTranslation from 'next-translate/useTranslation';
import {
  style,
  User,
  useRafflesByLabel,
  usePulledArtists,
  Artist,
} from '../../api';

import Loader from '../common/Loader';
import {
  Text,
  TextBold22,
  TextBold12,
  TextRegular14,
  TextBold14,
} from '../common/Text';
import { Play } from '../common/Icons';

import RaffleCard from '../Contest/Card';

import { desktopWidth, useIsDesktop } from '../../style/constants';
import { ProfileLink } from '../common/Link';
import UserImage from '../common/UserImage';
import LoadMoreButton from '../Contest/LoadMoreButton';
import { FollowButton } from '../common/Buttons/V2';

/**
 * Somebody set a fixed width to the slick carousel, so its not quite responsive as it should be
 *
 * && - Fixes the problem to target the right element
 */

const ResponsiveCard = styled(RaffleCard)`
  margin-right: 21px;

  && {
    width: auto;
  }
`;

interface Props {
  userId: number;
}

const BaseSlider: React.FunctionComponent = ({ children }) => {
  const dragging = useRef(false);

  return (
    <Slider
      centerMode={false}
      arrows={false}
      swipeToSlide
      slidesToShow={2.2}
      infinite={false}
      initialSlide={0}
      responsive={[
        {
          breakpoint: desktopWidth,
          settings: {
            slidesToShow: 1.2,
          },
        },
      ]}
      beforeChange={() => {
        dragging.current = true;
      }}
      afterChange={() => {
        dragging.current = false;
      }}
    >
      {React.Children.map(children, (child) => {
        const onPreventClick = (
          e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        ) => {
          if (dragging.current) {
            e.preventDefault();
          }

          return dragging.current;
        };

        return React.cloneElement(
          child as React.ReactElement<React.ComponentProps<typeof RaffleCard>>,
          {
            onClick: onPreventClick,
          },
        );
      })}
    </Slider>
  );
};

const RafflesSection = ({ userId }: Props) => {
  const { t } = useTranslation('profile');
  const { data: contest, isLoading } = useRafflesByLabel(userId, {
    filter: 'contest',
  });

  const sorted_contest = useMemo(
    () => orderBy(contest, ['closed_at'], ['desc']),
    [contest],
  );

  if (isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  if (!(Array.isArray(sorted_contest) && sorted_contest.length > 0)) {
    return null;
  }

  return (
    <Section>
      <SectionLabel>{t('profile:raffleHeaderLabel')}</SectionLabel>
      <BaseSlider>
        {sorted_contest.map((raffle) => (
          <ResponsiveCard raffle={raffle} key={raffle.id} />
        ))}
      </BaseSlider>
    </Section>
  );
};

const ContestsSection = ({ userId }: Props) => {
  const { t } = useTranslation('profile');
  const { data: remix_contest, isLoading } = useRafflesByLabel(userId, {
    filter: 'remix_contest',
  });

  const sorted_remix_contest = useMemo(
    () => orderBy(remix_contest, ['closed_at'], ['desc']),
    [remix_contest],
  );

  if (
    !(Array.isArray(sorted_remix_contest) && sorted_remix_contest.length > 0)
  ) {
    return null;
  }

  if (isLoading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <Section>
      <SectionLabel>{t('profile:contestsHeaderLabel')}</SectionLabel>
      <BaseSlider>
        {sorted_remix_contest.map((raffle) => (
          <ResponsiveCard raffle={raffle} key={raffle.id} />
        ))}
      </BaseSlider>
    </Section>
  );
};

const Break = styled.div`
  flex-basis: 100%;
  height: 0;
`;

const ArtistsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 14px;

  .tooltip-container {
    margin-bottom: 20px;
    &:not(:last-child) {
      margin-right: 20px;
    }
  }

  .react-tooltip {
    padding: 0;
    box-shadow: 1px 2px 3px #00000029;
  }

  .react-tooltip:before {
    content: none;
  }

  .react-tooltip:after {
    content: none;
  }

  .__react_component_tooltip.show {
    opacity: 1;
  }
`;

const PopperElement = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  ${UserImage} {
    margin-bottom: 15px;
  }
`;

const ArtistAvatar = ({ artist }: { artist: Artist }) => {
  const imageRef = useRef<HTMLImageElement | null>(null);

  /**
   * Simple fail safe protection for invalid images,
   * Not sure why they are any, but should be handled one way or another.
   */
  const onError = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.src = 'https://dev.virpp.com/Default-user.png';
    }
  }, []);

  return (
    <UserImage
      ref={imageRef}
      src={artist.profile_photo}
      size={44}
      alt={`user-avatar-${artist.id}`}
      onError={onError}
    />
  );
};

const ArtistItem = ({ artist }: { artist: Artist }) => {
  const { t } = useTranslation('profile');
  return (
    // const isMobile = /Mobi|Android/i.test(navigator.userAgent)

    <>
      <div
        className="tooltip-container"
        data-tip
        data-for={`artist-${artist.id}`}
        aria-describedby={`artist-${artist.id}`}
      >
        <ProfileLink profileId={artist.id}>
          <ArtistAvatar artist={artist} />
        </ProfileLink>
      </div>
      <ReactTooltip
        id={`artist-${artist.id}`}
        effect="solid"
        place="left"
        type="light"
        className="react-tooltip"
        delayHide={250}
        overridePosition={(
          { top, ...rest },
          _currentEvent,
          _currentTarget,
          node,
        ) => ({
          ...rest,
          /**
           * Node must be there otherwise, how do we get the element
           *
           * Since theres no option for "top-left" alignment in position
           * get the tooltip height and the avatar height and calculate the actual
           * top offset
           *
           * The addition "-1" is for box
           */
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          top: top + node.offsetHeight / 2 - 44 / 2 - 1,
        })}
        clickable
      >
        <PopperElement>
          <ArtistAvatar artist={artist} />
          <TextBold12>{artist.display_name}</TextBold12>
          <div style={{ marginTop: 15, marginBottom: 15 }}>
            <Play size={14} color="#000" />
            &nbsp;
            <TextBold14>{artist.plays}</TextBold14>
            &nbsp;
            <TextRegular14 style={{ color: style.grey_dark_new }}>
              {t('profile:plays')}
            </TextRegular14>
          </div>
          <FollowButton userId={artist.id} />
        </PopperElement>
      </ReactTooltip>
    </>
  );
};
const ArtistsSection = ({ userId }: Props) => {
  const { t } = useTranslation('profile');
  const isDesktop = useIsDesktop();

  const {
    records: data,
    total,
    fetchMore,
    isLoading: loading,
    canFetchMore,
  } = usePulledArtists(userId);

  if (!userId) {
    return null;
  }

  if (!(Array.isArray(data) && data.length > 0)) {
    return null;
  }

  if (loading) {
    return (
      <Section>
        <Loader />
      </Section>
    );
  }

  return (
    <Section>
      <SectionLabel>{t('profile:artistsPulled')}</SectionLabel>
      <TextBold22>{total}</TextBold22>
      <ArtistsContainer>
        {data.map((item, index) => {
          const artist = (item as unknown) as Artist;

          return (
            <Fragment key={artist.id}>
              {!!(isDesktop && index % 9 === 0) && <Break />}
              <ArtistItem artist={artist} />
            </Fragment>
          );
        })}
      </ArtistsContainer>
      {canFetchMore && (
        <div>
          <LoadMoreButton onClick={fetchMore} isFetching={loading} />
        </div>
      )}
    </Section>
  );
};

/**
 *
 * Profile for `user.role === label` got a revamp
 *
 * Task: https://yourbrandhub.atlassian.net/browse/VIR-1095
 * Design: https://xd.adobe.com/view/000f7dd6-a429-43e1-b639-9d93e7709c17-555c
 *
 */
const ProfileLabel = ({ user }: { user: User }) => {
  const { t } = useTranslation('profile');
  return (
    <>
      <Section>
        <SectionLabel>{t('profile:about')}</SectionLabel>
        {/*
         * @todo
         *
         * By design this should be html.
         * As discussed with the team we need to wrap this with: https://github.com/remarkablemark/html-react-parser
         *
         * And also add an editor on the profile edit page
         *
         * Suggested to use: https://github.com/tinymce/tinymce-react
         *
         */}
        <Text fontSize="22px" color={style.black_new}>
          {user?.about}
        </Text>
      </Section>
      <RafflesSection userId={user?.id} />
      <ContestsSection userId={user?.id} />
      <ArtistsSection userId={user?.id} />
    </>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  &:nth-child(n + 2) {
    margin-top: 30px;
  }
`;

const SectionLabel = styled.p`
  font-weight: bold;
  font-size: 10px;
  color: ${style.grey_dark_new};
  margin-bottom: 10px;
  text-transform: uppercase;
`;

export default ProfileLabel;
